import { Component, Input, OnChanges } from '@angular/core';
import { MultiShipmentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  MULTI_SHIPMENT_PREVIEW,
  PILL_CORNER_RADIUS_PX,
  PreviewProduct,
  productAssignments,
  resolveLabelTokens,
  unassignedQuantity,
} from '../../multi-shipment-example.model';

/** A resolved assignment pill ready to render next to a line item. */
interface AssignmentTag {
  label: string;
  backgroundColor: string;
  textColor: string;
  radiusPx: number;
}

interface OrderSummaryRow {
  name: string;
  variant: string;
  quantity: number;
  price: string;
  thumbnail: string;
  tags: AssignmentTag[];
}

@Component({
  selector: 'multi-shipment-order-summary-preview',
  templateUrl: './order-summary-preview.component.html',
  styleUrls: ['./order-summary-preview.component.scss'],
  standalone: false,
})
export class MultiShipmentOrderSummaryPreviewComponent implements OnChanges {
  @Input() lineItemAssignmentStatus: MultiShipmentExtension.LineItemAssignmentStatus;

  readonly totals = MULTI_SHIPMENT_PREVIEW.orderTotals;
  rows: OrderSummaryRow[] = [];

  ngOnChanges(): void {
    this.rows = MULTI_SHIPMENT_PREVIEW.products.map((product) => ({
      name: product.name,
      variant: product.variant,
      quantity: product.quantityInCart,
      price: product.price,
      thumbnail: product.thumbnail,
      tags: this.buildTags(product),
    }));
  }

  private buildTags(product: PreviewProduct): AssignmentTag[] {
    const assignedConfig = this.lineItemAssignmentStatus?.assigned;
    const unassignedConfig = this.lineItemAssignmentStatus?.unassigned;
    const tags: AssignmentTag[] = [];

    if (assignedConfig) {
      productAssignments(product).forEach((assignment) => {
        tags.push({
          label: resolveLabelTokens(assignedConfig.label, {
            quantity: assignment.quantity,
            name: assignment.recipientName,
            number: assignment.recipientNumber,
          }),
          backgroundColor: assignedConfig.backgroundColor,
          textColor: assignedConfig.textColor,
          radiusPx: PILL_CORNER_RADIUS_PX[assignedConfig.cornerRadius] ?? PILL_CORNER_RADIUS_PX.small,
        });
      });
    }

    const remaining = unassignedQuantity(product);
    if (unassignedConfig && remaining > 0) {
      tags.push({
        label: resolveLabelTokens(unassignedConfig.label, { quantity: remaining }),
        backgroundColor: unassignedConfig.backgroundColor,
        textColor: unassignedConfig.textColor,
        radiusPx: PILL_CORNER_RADIUS_PX[unassignedConfig.cornerRadius] ?? PILL_CORNER_RADIUS_PX.small,
      });
    }

    return tags;
  }
}
